/**
 * PlacementsProvider — hand-placement overrides as a workspace-scoped service.
 *
 * Promotes the `usePlacements` data layer (drag overrides + localStorage
 * persistence + undo/redo history) from an App-owned hook to a shared context,
 * so every board surface in a composition reads ONE controller. Mounting it
 * once means the persisted placements + undo stack are the single source of
 * truth — the viewer's board, the editable workbench board, and any chrome that
 * drives reset/undo/redo all agree.
 *
 * Like {@link ProcessPlaybackProvider} this is a provider, not a store slice:
 * the history bookkeeping (debounced commit, undo/redo stacks) is imperative
 * controller state, not flat reactive data. Compositions with no provider
 * (marketing demo, Studio preview) simply don't mount it — `BoardPane` reads
 * the optional accessor and the board falls back to ephemeral local placements.
 */
import { type ReactNode } from "react";
import { usePlacements } from "../hooks/usePlacements.js";
import { PlacementsContext } from "./PlacementsContext.js";

export function PlacementsProvider({
  children,
}: {
  children: ReactNode;
}): ReactNode {
  const placements = usePlacements();
  return (
    <PlacementsContext.Provider value={placements}>
      {children}
    </PlacementsContext.Provider>
  );
}
